import type { JSX } from 'react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { ECampaignObjective } from '@feathr/blackbox';
import { Alert, ConfirmModal, List } from '@feathr/components';
import { useObjectiveUtils } from '@feathr/extender/hooks';

interface IObjectiveChangeConfirmationModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentObjective: ECampaignObjective;
  newObjective: ECampaignObjective;
}

import * as styles from './ObjectiveChangeConfirmationModal.css';

function ObjectiveChangeConfirmationModal({
  opened,
  onClose,
  onConfirm,
  currentObjective,
  newObjective,
}: Readonly<IObjectiveChangeConfirmationModalProps>): JSX.Element {
  const { t } = useTranslation();
  const { objectiveText } = useObjectiveUtils();

  // Get objective names with fallbacks
  const currentObjectiveName = objectiveText[currentObjective]?.name ?? currentObjective;
  const newObjectiveName = objectiveText[newObjective]?.name ?? newObjective;

  const alertDescription = (
    <List hasBullets={true}>
      <List.Item>{t('Targeting and audience settings will be reset')}</List.Item>
      <List.Item>{t('Bidding strategy will be reconfigured')}</List.Item>
      <List.Item>{t('Available ad formats may change')}</List.Item>
      <List.Item>{t('Optimization metrics will be updated')}</List.Item>
    </List>
  );

  return (
    <ConfirmModal
      confirmationHelpText={t('Resetting will redirect you back to the Audience step.')}
      confirmationPhrase={'RESET'}
      confirmButtonText={t('Reset')}
      confirmButtonType={'danger'}
      onClose={onClose}
      onConfirm={onConfirm}
      opened={opened}
      t={t}
      title={t('Change Campaign Objective?')}
    >
      <div>
        <p>
          <Trans
            values={{
              currentObjective: currentObjectiveName,
              newObjective: newObjectiveName,
            }}
          >
            You're switching from <strong>{{ currentObjective }}</strong> to{' '}
            <strong>{{ newObjective }}</strong>.
          </Trans>
        </p>
        <p>
          <Trans>
            <strong>This will reset your campaign progress</strong> because these objectives use
            different optimization strategies and available features.
          </Trans>
        </p>
      </div>
      <Alert
        className={styles.alert}
        description={alertDescription}
        title={t('What will be affected:')}
        type={'warning'}
      />
      <p>{t('Your campaign name and basic information will be preserved.')}</p>
    </ConfirmModal>
  );
}

export default ObjectiveChangeConfirmationModal;
